import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageNavbar from './LegalPageNavbar';
import './LandingPage.css';

const SYLLERA_TAGLINE =
  'Master Languages Vocabulary by Educational Level with AI-powered learning';

const PrivacyPolicyPage = () => (
  <div className="landing-page">
    <LegalPageNavbar />

    <section className="section-container" style={{ paddingTop: '96px', paddingBottom: '72px' }}>
      <div className="section-content legal-document" style={{ maxWidth: 900 }}>
        <h1>Privacy Policy</h1>
        <p><strong>Last updated:</strong> June 16, 2026</p>
        <p>
          <strong>Syllera</strong> ({SYLLERA_TAGLINE}) (&quot;Syllera,&quot; &quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;) operates an AI-powered educational platform that helps students and teachers master
          language vocabulary by educational level. This Privacy Policy explains how we collect, use, store, share,
          and protect your personal information when you use our website, mobile applications, and related services
          (collectively, the &quot;Platform&quot;).
        </p>
        <p>
          By using Syllera, you agree to the collection and use of information in accordance with this policy.
          If you do not agree, please do not use the Platform.
        </p>

        <h2>1. Introduction</h2>
        <p>
          Syllera is an AI-powered language learning platform serving students, teachers, parents, schools, and
          partners. Our mission is to help learners master language vocabulary by educational level with
          AI-powered learning. Because our users include young learners, we take privacy seriously and design our
          practices to protect personal data.
        </p>

        <h2>2. Information We Collect</h2>
        <p>We may collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account information:</strong> name, email address, username, password (stored in encrypted
            form), role (student, teacher, parent, school, partner), and profile details you choose to provide.
          </li>
          <li>
            <strong>Learning and usage data:</strong> vocabulary progress, quiz results, course activity, study
            streaks, educational level selections, language preferences, AI interaction history, and other
            learning analytics used to personalize your experience.
          </li>
          <li>
            <strong>Technical data:</strong> IP address, browser type, device identifiers, operating system,
            app version, log files, and general usage patterns (pages visited, features used, session duration).
          </li>
          <li>
            <strong>Communication data:</strong> messages you send to support, feedback, survey responses, and
            records of help requests.
          </li>
          <li>
            <strong>Payment and subscription data:</strong> billing details, transaction history, and subscription
            status when you purchase paid plans. Payment card details are processed by our payment providers and
            are not stored directly by Syllera.
          </li>
          <li>
            <strong>School and parental data:</strong> information related to school accounts, class enrollment,
            parent–child links, and parental control settings where applicable.
          </li>
          <li>
            <strong>Cookies and similar technologies:</strong> see Section 8 and our{' '}
            <Link to="/cookie-policy">Cookie Policy</Link>.
          </li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use personal information to:</p>
        <ul>
          <li>Create, manage, and secure your account</li>
          <li>Deliver personalized learning paths, vocabulary recommendations, and AI-powered study features</li>
          <li>Track learning progress and provide reports to students, teachers, parents, and schools where authorized</li>
          <li>Improve Platform performance, content quality, and user experience</li>
          <li>Process subscriptions, payments, and partner referrals</li>
          <li>Send service-related communications (account alerts, progress updates, security notices)</li>
          <li>Respond to support requests and enforce our Terms of Service</li>
          <li>Comply with legal obligations and protect against fraud or abuse</li>
        </ul>
        <p>
          We do not sell your personal information. We use data to operate and improve Syllera, not to sell
          personal profiles to advertisers.
        </p>
        <p>
          AI-generated insights and recommendations are used to support learning only. AI-generated content is
          provided for educational support purposes only and may contain inaccuracies. Users should independently
          verify important information.
        </p>

        <h2>4. How We Share Information</h2>
        <p>We may share information only in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service providers:</strong> trusted third parties that help us operate the Platform, such as
            cloud hosting, email delivery (e.g., SendGrid or SMTP providers), push notification services (e.g.,
            Firebase Cloud Messaging), live chat support (e.g., Tawk.to), analytics, and payment processing.
            These providers may access data only to perform services on our behalf and are required to protect it.
          </li>
          <li>
            <strong>Schools and authorized adults:</strong> teachers, parents, or school administrators may
            access student progress where accounts are linked and permissions allow.
          </li>
          <li>
            <strong>Legal requirements:</strong> when required by law, regulation, court order, or to protect
            the rights, safety, and security of Syllera, our users, or others.
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets,
            subject to continued protection of your data.
          </li>
        </ul>
        <p>
          <strong>We do not sell personal information.</strong> We do not share student data for third-party
          marketing purposes.
        </p>

        <h2>5. Your Rights and Choices</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul>
          <li><strong>Access</strong> the personal information we hold about you</li>
          <li><strong>Update or correct</strong> inaccurate account or profile information</li>
          <li><strong>Delete</strong> your account and associated personal data, subject to legal retention requirements</li>
          <li><strong>Export</strong> certain learning or account data where technically available</li>
          <li><strong>Object to or restrict</strong> certain processing activities where applicable law provides this right</li>
          <li><strong>Withdraw consent</strong> where processing is based on consent (without affecting prior lawful processing)</li>
          <li><strong>Manage communications</strong> through account settings or unsubscribe links in non-essential emails</li>
        </ul>
        <p>
          To exercise these rights, contact us at{' '}
          <a href="mailto:privacy@syllera.com">privacy@syllera.com</a> or{' '}
          <a href="mailto:support@syllera.com">support@syllera.com</a>. We will respond within a reasonable
          timeframe as required by applicable law.
        </p>

        <h2>6. Data Security</h2>
        <p>
          We implement administrative, technical, and organizational measures designed to protect personal
          information against unauthorized access, loss, misuse, or alteration. These measures include encrypted
          connections (HTTPS), access controls, secure authentication practices, and monitoring of our systems.
        </p>
        <p>
          No method of transmission or storage is completely secure. While we work to protect your data, we cannot
          guarantee absolute security. Please use a strong password and keep your login credentials confidential.
        </p>
        <p>
          To the maximum extent permitted by law, Syllera shall not be held liable for any indirect, incidental,
          special, consequential, or punitive damages resulting from unauthorized access to your data that occurs
          despite our reasonable security measures.
        </p>

        <h2>7. Children&apos;s Privacy</h2>
        <p>
          Syllera is designed for educational use and may be used by students under the age of 18, including
          children in primary, middle, and high school. We are committed to protecting children&apos;s privacy.
        </p>
        <ul>
          <li>
            Student accounts for minors should be created with appropriate school, teacher, or parental oversight
            where required by law.
          </li>
          <li>
            We collect only information reasonably necessary to provide educational services, track learning
            progress, and maintain account security.
          </li>
          <li>
            We do not knowingly sell or share children&apos;s personal information for behavioral advertising.
          </li>
          <li>
            Parents, guardians, and authorized school officials may contact us to review, update, or request
            deletion of a minor&apos;s information where permitted by law.
          </li>
        </ul>
        <p>
          If you believe we have collected information from a child without proper authorization, please contact
          us at <a href="mailto:privacy@syllera.com">privacy@syllera.com</a> so we can take appropriate action.
        </p>

        <h2>8. Cookies and Tracking Technologies</h2>
        <p>
          We use cookies, local storage, and similar technologies to keep you signed in, remember preferences,
          understand how the Platform is used, and improve performance. For full details, see our{' '}
          <Link to="/cookie-policy">Cookie Policy</Link>.
        </p>
        <ul>
          <li><strong>Essential cookies:</strong> required for login, security, and core functionality</li>
          <li><strong>Preference cookies:</strong> remember language, settings, and display choices</li>
          <li><strong>Analytics cookies:</strong> help us understand usage patterns and improve the Platform</li>
        </ul>
        <p>
          You can control cookies through your browser settings. Disabling certain cookies may limit some
          features. Our mobile apps may use device identifiers for similar purposes.
        </p>

        <h2>9. Data Retention</h2>
        <p>
          We retain personal information for as long as your account is active or as needed to provide services,
          comply with legal obligations, resolve disputes, and enforce agreements. When you delete your account,
          we will delete or anonymize personal data within a reasonable period, except where retention is
          required by law or for legitimate business purposes such as fraud prevention.
        </p>

        <h2>10. International Data Transfers</h2>
        <p>
          Syllera may process and store information on servers located in different countries. Where required,
          we take steps to ensure appropriate safeguards are in place when personal data is transferred
          internationally.
        </p>

        <h2>11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we will post the
          updated policy on this page and update the &quot;Last updated&quot; date. Continued use of the Platform
          after changes become effective constitutes acceptance of the revised policy.
        </p>

        <h2>12. Contact Us</h2>
        <p>For privacy-related questions, requests, or concerns, please contact:</p>
        <ul>
          <li>Email: <a href="mailto:privacy@syllera.com">privacy@syllera.com</a></li>
          <li>Support: <a href="mailto:support@syllera.com">support@syllera.com</a></li>
          <li>Website: <Link to="/contact">syllera.com/contact</Link></li>
        </ul>
        <p>
          Related policies: <Link to="/terms-of-service">Terms of Service</Link> ·{' '}
          <Link to="/cookie-policy">Cookie Policy</Link> · <Link to="/refund-policy">Refund Policy</Link>
        </p>
      </div>
    </section>
  </div>
);

export default PrivacyPolicyPage;
